import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnChanges,
  OnInit,
  AfterViewInit
} from "@angular/core";
import { Subscription, fromEvent, debounceTime, BehaviorSubject, startWith } from "rxjs";
import { CdrSummaryStatisticOrgService } from "@onsip/common/services/api/resources/cdrSummaryStatistic/cdr-summary-statistic-org.service";
import { CdrSummaryStatisticOrg } from "@onsip/common/services/api/resources/cdrSummaryStatistic/cdr-summary-statistic";
import { views } from "@onsip/web/app/phone/views";
import { FormatE164Pipe } from "@onsip/web/features/shared/pipes/format-e164.pipe";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { LineGraphData } from "../charts/lineGraph/line-graph.component";
import { DatePipe } from "@angular/common";
//import { PrepaidTransactionService } from "@onsip/common/services/api/resources/prepaidTransaction/prepaid-transaction.service";
//import { currencyFormatter } from "@onsip/web/features/shared/helpers/currency-formatter";

interface CachedResult {
  inboundData: Array<LineGraphSubData>;
  outboundData: Array<LineGraphSubData>;
  avgInboundCalls: number;
  avgOutboundCalls: number;
  //callingCreditsUsed: string;
  emptyState: boolean;
}

interface LineGraphSubData {
  x: Date;
  y: number;
}

@Component({
  selector: "onsip-pbx-inbound-outbound-calling-trends",
  templateUrl: "./inbound-outbound-calling-trends.component.html",
  styleUrls: ["./inbound-outbound-calling-trends.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormatE164Pipe],
  standalone: false
})
export class InboundOutboundCallingTrendsComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() dateStart!: string;
  @Input() dateEnd!: string;

  trendData: Array<LineGraphData> = [];
  inboundData: Array<LineGraphSubData> = [];
  outboundData: Array<LineGraphSubData> = [];
  coveredDatesSet: Set<string> = new Set<string>();
  cachedResult: Record<string, CachedResult> = {};
  dateRange = this.dateStart + "-" + this.dateEnd;
  totalInboundCalls = 0;
  totalOutboundCalls = 0;
  callingCreditsUsed!: string;
  avgInboundCalls!: number;
  avgOutboundCalls!: number;
  noData = true;
  lineGraphWidth = 500;
  views = views;
  loadingStateComplete$ = new BehaviorSubject(true);

  constructor(
    private cdrOrgService: CdrSummaryStatisticOrgService,
    //private prepaidTransactionService: PrepaidTransactionService,
    private cdRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private snackbar: SnackbarService,
    private hostElement: ElementRef
  ) {}

  private unsubscriber = new Subscription();

  ngOnInit() {
    this.initChart();
  }

  ngAfterViewInit(): void {
    this.handleWindowResize();
  }

  ngOnChanges(): void {
    this.initChart();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  private initChart(): void {
    this.dateRange = this.dateStart + "-" + this.dateEnd;
    if (this.cachedResult[this.dateRange]) {
      this.inboundData = this.cachedResult[this.dateRange].inboundData;
      this.outboundData = this.cachedResult[this.dateRange].outboundData;
      this.avgInboundCalls = this.cachedResult[this.dateRange].avgInboundCalls;
      this.avgOutboundCalls = this.cachedResult[this.dateRange].avgOutboundCalls;
      //this.callingCreditsUsed = this.cachedResult[this.dateRange].callingCreditsUsed;
      this.trendData = [
        { name: "Inbound", data: this.inboundData },
        { name: "Outbound", data: this.outboundData }
      ];
      this.noData = this.cachedResult[this.dateRange].emptyState;
      return;
    }
    this.getCallingTrends();
    //this.getCallingCreditsUsed();
  }

  private getCallingTrends(): void {
    this.loadingStateComplete$.next(false);
    this.cdrOrgService
      .cdrSummaryStatisticOrg({ StartDateTime: this.dateStart, EndDateTime: this.dateEnd })
      .then(res => {
        if (res.status === "success") {
          this.inboundData = [];
          this.outboundData = [];
          this.totalInboundCalls = 0;
          this.totalOutboundCalls = 0;
          const inOutCdrs = Object.values(res.data);
          this.noData = inOutCdrs.length > 0 ? false : true;
          inOutCdrs.map(cdr => {
            this.cdrToInboundOutboundData(cdr);
          });
          this.getAverageInboundOutboundCalls();
          this.generateEmptyCdrs();
          this.inboundData.sort((a, b) => {
            return a.x.getTime() - b.x.getTime();
          });
          this.outboundData.sort((a, b) => {
            return a.x.getTime() - b.x.getTime();
          });
          this.trendData = [
            { name: "Inbound", data: this.inboundData },
            { name: "Outbound", data: this.outboundData }
          ];
          this.storeToCache();
          this.cdRef.markForCheck();
        } else {
          this.snackbar.openSnackBar(res.data.message, "error");
          this.cdrOrgService.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
  }

  // private getCallingCreditsUsed(): void {
  //   this.prepaidTransactionService
  //     .prepaidTransactionBrowse({ startDateTime: this.dateStart, endDateTime: this.dateEnd })
  //     .then(res => {
  //       if (res.status === "success") {
  //         const prepaidTransactions = Object.values(res.data);
  //         let summedCreditsUsed = 0;
  //         prepaidTransactions.forEach(trans => {
  //           summedCreditsUsed += -1 * Number(trans.amount);
  //         });
  //         this.callingCreditsUsed = currencyFormatter(summedCreditsUsed);
  //         this.cachedResult[this.dateRange].callingCreditsUsed = this.callingCreditsUsed;
  //         this.cdRef.markForCheck();
  //       }
  //     });
  // }

  private cdrToInboundOutboundData(cdr: CdrSummaryStatisticOrg): void {
    const cdrInboundCalls = Number(cdr.origination);
    this.inboundData.push({
      x: new Date(cdr.calldate.replace(/-/g, "/")),
      y: cdrInboundCalls
    });
    this.totalInboundCalls += cdrInboundCalls;
    const cdrOutboundCalls = Number(cdr.termination);
    this.outboundData.push({
      x: new Date(cdr.calldate.replace(/-/g, "/")),
      y: cdrOutboundCalls
    });
    this.totalOutboundCalls += cdrOutboundCalls;
    this.coveredDatesSet.add(this.getDatePipeTransform(new Date(cdr.calldate.replace(/-/g, "/"))));
  }

  private generateEmptyCdrs(): void {
    // fill in missing dates with empty CDRs
    const now = new Date();
    for (
      let d = new Date(this.dateStart);
      d <= now && d <= new Date(this.dateEnd);
      d.setDate(d.getDate() + 1)
    ) {
      const curDate = new Date(d);
      const curDateStr = this.getDatePipeTransform(curDate);
      if (!this.coveredDatesSet.has(curDateStr)) {
        this.inboundData.push({
          x: curDate,
          y: 0
        });
        this.outboundData.push({
          x: curDate,
          y: 0
        });
      }
    }
  }

  private storeToCache(): void {
    this.cachedResult[this.dateRange] = {
      inboundData: [],
      outboundData: [],
      avgInboundCalls: 0,
      avgOutboundCalls: 0,
      callingCreditsUsed: "",
      emptyState: true
    } as CachedResult;
    this.cachedResult[this.dateRange].inboundData = this.inboundData;
    this.cachedResult[this.dateRange].outboundData = this.outboundData;
    this.cachedResult[this.dateRange].avgInboundCalls = this.avgInboundCalls;
    this.cachedResult[this.dateRange].avgOutboundCalls = this.avgOutboundCalls;
    this.cachedResult[this.dateRange].emptyState = this.noData;
  }

  private getDaysBetween(startDate: Date, endDate: Date): number {
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }

  private getAverageInboundOutboundCalls(): void {
    const daysInInterval = this.getDaysBetween(
      new Date(this.dateStart.replace(/-/g, "/")),
      new Date(this.dateEnd.replace(/-/g, "/"))
    );
    this.avgInboundCalls = Number((this.totalInboundCalls / daysInInterval).toFixed(2));
    this.avgOutboundCalls = Number((this.totalOutboundCalls / daysInInterval).toFixed(2));
  }

  private handleWindowResize(): void {
    // adjust width of the line graph depending on the current window size if that changes
    this.unsubscriber.add(
      fromEvent(window, "resize")
        .pipe(startWith(true), debounceTime(500))
        .subscribe(() => {
          this.adjustLineGraphWidth();
          this.cdRef.markForCheck();
        })
    );
  }

  private getDatePipeTransform(date: Date) {
    return this.datePipe.transform(date, "yyyy-MM-dd 00:00") || "";
  }

  adjustLineGraphWidth(): void {
    this.lineGraphWidth = this.hostElement.nativeElement.offsetWidth;
    if (window.innerWidth >= 1280) {
      // remove width space for the summary stats
      this.lineGraphWidth -= 360;
    }
  }
}
